/**
 * Semantic colour roles.
 *
 * Components reference roles ("text.secondary"), never seeds ("ink") and never
 * raw hex. Swapping the seed palette therefore restyles the whole product.
 *
 * Translucent roles are produced by {@link withAlpha}; large filled surfaces
 * are pre-flattened against their known background so React Native does not
 * pay for overdraw and so two tints can never stack into a darker one.
 */

import { alpha, flatten, withAlpha } from './alpha';
import { seed } from './palette';

const onCream = (hex: string, a: number): string => flatten(hex, a, seed.cream);
const onWhite = (hex: string, a: number): string => flatten(hex, a, seed.white);

/**
 * Status tones. Domain code maps each donation/payment status onto one of
 * these; nothing outside this file decides what colour a status is.
 *
 * Resolves gap G-06 (the artwork showed "Preparing" as green to donors and
 * amber to admins). One tone per status, both surfaces, no exceptions:
 * in-progress work is amber so it is distinguishable from completed work at a
 * glance — which the donor list could not do when both were green.
 */
export type StatusTone = 'neutral' | 'info' | 'progress' | 'success' | 'danger';

export interface ToneColors {
  /** Text and icon colour. Always meets 4.5:1 against `bg`. */
  fg: string;
  /** Pill / badge fill, flattened against a white card. */
  bg: string;
  /** Solid dot or bar, e.g. chart legends and timeline nodes. */
  solid: string;
  /** Border for outlined variants. */
  border: string;
}

const tone = (base: string, fg: string = base): ToneColors => ({
  fg,
  bg: onWhite(base, alpha.a20),
  solid: base,
  border: onWhite(base, alpha.a32),
});

export const statusTone: Record<StatusTone, ToneColors> = {
  neutral: {
    fg: withAlpha(seed.ink, alpha.a64),
    bg: onWhite(seed.ink, alpha.a08),
    solid: withAlpha(seed.ink, alpha.a44),
    border: onWhite(seed.ink, alpha.a16),
  },
  info: tone(seed.blue),
  // Amber needs more body than green or blue to register as a fill.
  progress: {
    fg: '#8A4B00', // orange darkened for 4.5:1 on its own tint
    bg: onWhite(seed.orange, alpha.a40),
    solid: seed.orange,
    border: onWhite(seed.orange, alpha.a40),
  },
  success: tone(seed.greenBright, seed.green),
  danger: tone(seed.red),
};

export const colors = {
  bg: {
    /** Warm cream page background. Every donor screen. */
    page: seed.cream,
    /** Cards, header, sheets. */
    surface: seed.white,
    /** Rows inside a card that need separation without a border. */
    surfaceSunken: onWhite(seed.ink, alpha.a04),
    /** Deep green: impact footer, profile card, promotional banners. */
    brand: seed.green,
    /** Light green wash: step circles, impact cards, selected meal card. */
    brandSubtle: onCream(seed.greenBright, alpha.a20),
    /** Selected/active row fill inside a light surface. */
    selected: onWhite(seed.greenBright, alpha.a16),
    /**
     * A warm wash for something new rather than something to do — Home's
     * New Features card — so it reads apart from the green actions.
     */
    highlight: onWhite(seed.orange, alpha.a08),
    /** A short bright rule under a headline, as on Home's hero. */
    accent: seed.greenBright,
    /** Pressed state over any light surface. */
    pressed: withAlpha(seed.ink, alpha.a08),
    /** Behind the navigation drawer and modals. */
    scrim: withAlpha(seed.ink, alpha.a72),
    /** Skeleton placeholder blocks. */
    skeleton: onWhite(seed.ink, alpha.a08),
    /** Admin chrome. */
    adminSidebar: seed.navy,
    adminSidebarActive: withAlpha(seed.greenBright, alpha.a24),
    adminPage: onWhite(seed.ink, alpha.a04),
  },

  text: {
    /** Body copy and values. Softened from pure ink, which reads as harsh. */
    primary: withAlpha(seed.ink, alpha.a88),
    /** Field labels, card descriptions, list subtitles. */
    secondary: withAlpha(seed.ink, alpha.a64),
    /** Timestamps, helper text, captions. */
    tertiary: withAlpha(seed.ink, alpha.a56),
    /** Placeholders and disabled controls. */
    disabled: withAlpha(seed.ink, alpha.a44),
    /** Prices, active nav labels, section emphasis. */
    brand: seed.green,
    /** On the deep-green footer, banners and primary buttons. */
    onBrand: seed.white,
    /** Secondary copy on deep green, e.g. footer stat captions. */
    onBrandMuted: withAlpha(seed.white, alpha.a72),
    /** Logout, destructive actions, error messages. */
    danger: seed.red,
    /** Inline links. */
    link: seed.green,
  },

  border: {
    /** Row dividers inside cards. */
    subtle: withAlpha(seed.ink, alpha.a08),
    /** Card outlines, input rest state. */
    default: withAlpha(seed.ink, alpha.a12),
    /** Emphasised separators. */
    strong: withAlpha(seed.ink, alpha.a24),
    /** Selected meal card, active input. */
    brand: seed.green,
    /** Focus ring, drawn outside the control. */
    focus: withAlpha(seed.greenBright, alpha.a32),
    /** Dividers on the deep-green footer band. */
    onBrand: withAlpha(seed.white, alpha.a24),
  },

  action: {
    primaryBg: seed.green,
    primaryText: seed.white,
    /** Deep green darkened by compositing ink over it — no new seed needed. */
    primaryPressed: flatten(seed.ink, alpha.a16, seed.green),
    primaryDisabled: onWhite(seed.green, alpha.a32),

    secondaryBg: 'transparent',
    secondaryText: seed.green,
    secondaryBorder: seed.green,
    secondaryPressed: withAlpha(seed.green, alpha.a08),

    dangerBg: seed.red,
    dangerText: seed.white,
    dangerSubtle: onWhite(seed.red, alpha.a08),
  },

  status: statusTone,

  /**
   * Chart roles for the admin dashboard.
   *
   * Every dashboard chart plots one measure, so it wears one hue: `accent`,
   * the bright brand green, which clears 3:1 on a white card. A comparison
   * (the previous period) wears `comparison`, a grey that recedes so the
   * current period stays the loud line. Grid and axis are hairlines one step
   * off the surface. Status breakdowns use `status.*.solid` with a text label,
   * never these.
   */
  chart: {
    accent: seed.greenBright,
    /** Area under the accent line: a wash, never a block. */
    accentWash: withAlpha(seed.greenBright, alpha.a12),
    comparison: onWhite(seed.ink, alpha.a44),
    grid: onWhite(seed.ink, alpha.a08),
    axis: onWhite(seed.ink, alpha.a24),
    /** The unfilled part of a meter or bar track. */
    track: onWhite(seed.greenBright, alpha.a16),
    /** Hover band behind the bar or day under the pointer. */
    cursor: withAlpha(seed.ink, alpha.a04),
  },
} as const;

export type Colors = typeof colors;
